<template>
  <div class="subscription-overview">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="mb-0">
        <AppIcon name="bi-speedometer2" class="bi bi-speedometer2 me-2" />
        {{ $t('subscription_overview') }}
      </h4>
      <div class="action-buttons d-flex gap-2">
        <button 
          @click="refreshData" 
          class="btn btn-outline-primary btn-sm"
          :disabled="loading"
        >
          <AppIcon name="bi-arrow-clockwise" class="bi bi-arrow-clockwise" :class="{ 'spinning': loading }" />
          <span class="d-none d-md-inline ms-2">{{ $t('refresh') }}</span>
        </button>
        <router-link to="/subscriptions" class="btn btn-primary btn-sm">
          <AppIcon name="bi-list-ul" class="bi bi-list-ul me-2" />
          <span class="d-none d-md-inline">{{ $t('view_all') }}</span>
        </router-link>
      </div>
    </div>
    
    <!-- Main Metrics Cards -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="metric-card card h-100 border-0 shadow-sm hover-lift">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <p class="text-muted small mb-1">{{ $t('total_subscriptions') }}</p>
                <h2 class="mb-0 fw-bold">{{ totalSubscriptions }}</h2>
              </div>
              <div class="metric-icon bg-primary bg-opacity-10 rounded-3 p-2">
                <AppIcon name="bi-people-fill" class="bi bi-people-fill text-primary" style="font-size: 1.5rem;" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-3">
        <div class="metric-card card h-100 border-0 shadow-sm hover-lift">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <p class="text-muted small mb-1">{{ $t('active_subscriptions') }}</p>
                <h2 class="mb-0 fw-bold text-success">{{ activeSubscriptions }}</h2>
                <small class="text-muted">{{ activePercentage }}%</small>
              </div>
              <div class="metric-icon bg-success bg-opacity-10 rounded-3 p-2">
                <AppIcon name="bi-person-check-fill" class="bi bi-person-check-fill text-success" style="font-size: 1.5rem;" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-3">
        <div class="metric-card card h-100 border-0 shadow-sm hover-lift">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <p class="text-muted small mb-1">{{ $t('currently_in_gym') }}</p>
                <h2 class="mb-0 fw-bold text-warning">{{ currentClientsInGym }}</h2>
                <small class="text-muted">{{ $t('live') }}</small>
              </div>
              <div class="metric-icon bg-warning bg-opacity-10 rounded-3 p-2">
                <AppIcon name="bi-building" class="bi bi-building text-warning" style="font-size: 1.5rem;" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-3">
        <div class="metric-card card h-100 border-0 shadow-sm hover-lift">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <p class="text-muted small mb-1">{{ $t('today_attendance') }}</p>
                <h2 class="mb-0 fw-bold text-info">{{ todayAttendance }}</h2>
                <small class="text-muted">{{ $t('visits') }}</small>
              </div>
              <div class="metric-icon bg-info bg-opacity-10 rounded-3 p-2">
                <AppIcon name="bi-calendar-check-fill" class="bi bi-calendar-check-fill text-info" style="font-size: 1.5rem;" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Status Distribution and Occupancy -->
    <div class="row g-3 mb-4">
      <div class="col-md-6">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white border-bottom">
            <h6 class="mb-0 fw-semibold">
              <AppIcon name="bi-pie-chart-fill" class="bi bi-pie-chart-fill me-2" />
              {{ $t('subscription_status_distribution') }}
            </h6>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-6">
                <div class="status-item">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted small">{{ $t('active') }}</span>
                    <span class="badge bg-success">{{ activeSubscriptions }}</span>
                  </div>
                  <div class="progress" style="height: 8px;">
                    <div 
                      class="progress-bar bg-success" 
                      :style="{ width: getProgressPercentage(activeSubscriptions, totalSubscriptions) + '%' }"
                      role="progressbar"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="col-6">
                <div class="status-item">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted small">{{ $t('canceled') }}</span>
                    <span class="badge bg-danger">{{ canceledSubscriptions }}</span>
                  </div>
                  <div class="progress" style="height: 8px;">
                    <div 
                      class="progress-bar bg-danger" 
                      :style="{ width: getProgressPercentage(canceledSubscriptions, totalSubscriptions) + '%' }"
                      role="progressbar"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col-12">
                <div class="d-flex justify-content-around text-center pt-3 border-top">
                  <div>
                    <div class="h4 mb-0 text-success">{{ activePercentage }}%</div>
                    <small class="text-muted">{{ $t('active_rate') }}</small>
                  </div>
                  <div>
                    <div class="h4 mb-0 text-danger">{{ canceledPercentage }}%</div>
                    <small class="text-muted">{{ $t('canceled_rate') }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white border-bottom">
            <h6 class="mb-0 fw-semibold">
              <AppIcon name="bi-building" class="bi bi-building me-2" />
              {{ $t('gym_occupancy') }}
            </h6>
          </div>
          <div class="card-body">
            <div class="text-center mb-3">
              <div class="position-relative d-inline-block">
                <svg width="120" height="120" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#e9ecef" stroke-width="12"/>
                  <circle 
                    cx="60" 
                    cy="60" 
                    r="50" 
                    fill="none" 
                    stroke="#ffc107" 
                    stroke-width="12"
                    :stroke-dasharray="`${occupancyPercentage * 3.14} 314`"
                    transform="rotate(-90 60 60)"
                  />
                </svg>
                <div class="position-absolute top-50 start-50 translate-middle text-center">
                  <div class="h3 mb-0 fw-bold text-warning">{{ currentClientsInGym }}</div>
                  <small class="text-muted">{{ $t('inside') }}</small>
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-between align-items-center">
              <div class="text-center flex-fill">
                <div class="h5 mb-0">{{ occupancyPercentage }}%</div>
                <small class="text-muted">{{ $t('occupancy') }}</small>
              </div>
              <div class="text-center flex-fill border-start">
                <div class="h5 mb-0">{{ totalSubscriptions - currentClientsInGym }}</div>
                <small class="text-muted">{{ $t('available') }}</small>
              </div>
            </div>
            <div class="mt-3 p-2 bg-light rounded text-center">
              <small class="text-muted">
                <AppIcon name="bi-calendar-day" class="bi bi-calendar-day me-1" />
                {{ todayAttendance }} {{ $t('total_visits_today') }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Current Gym Attendees Table -->
    <div class="row">
      <div class="col-12">
        <div class="card border-0 shadow-sm">
          <div class="card-header bg-white border-bottom">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0 fw-semibold">
                <AppIcon name="bi-person-lines-fill" class="bi bi-person-lines-fill me-2" />
                {{ $t('current_gym_attendees') }}
                <span class="badge bg-warning ms-2">{{ currentAttendees.length }}</span>
              </h6>
              <button 
                v-if="currentAttendees.length > 0"
                @click="handleBulkCheckout"
                class="btn btn-sm btn-outline-danger"
                :disabled="loading"
              >
                <AppIcon name="bi-box-arrow-right" class="bi bi-box-arrow-right me-1" />
                {{ $t('checkout_all') }}
              </button>
            </div>
          </div>
          <div class="card-body p-0">
            <table-loading-bar v-if="loading && currentAttendees.length === 0" class="text-center py-5" />
            <div v-if="!loading" class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th class="px-4 py-3">
                      <AppIcon name="bi-person" class="bi bi-person me-1" />
                      {{ $t('client_name') }}
                    </th>
                    <th class="px-4 py-3">
                      <AppIcon name="bi-clock" class="bi bi-clock me-1" />
                      {{ $t('check_in_time') }}
                    </th>
                    <th class="px-4 py-3">
                      <AppIcon name="bi-hourglass-split" class="bi bi-hourglass-split me-1" />
                      {{ $t('duration') }}
                    </th>
                    <th class="px-4 py-3 text-end">{{ $t('action') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="attendee in currentAttendees" :key="attendee.id" class="align-middle">
                    <td class="px-4 py-3">
                      <div class="d-flex align-items-center">
                        <div class="avatar-circle bg-primary bg-opacity-10 text-primary me-2">
                          {{ getInitials(attendee.client.name) }}
                        </div>
                        <strong>{{ attendee.client.name }}</strong>
                      </div>
                    </td>
                    <td class="px-4 py-3">
                      <span class="text-muted">{{ formatTime(attendee.check_in) }}</span>
                    </td>
                    <td class="px-4 py-3">
                      <span class="badge bg-info">{{ calculateDuration(attendee.check_in) }}</span>
                    </td>
                    <td class="px-4 py-3 text-end">
                      <button 
                        @click="handleCheckout(attendee.id)"
                        :disabled="loading"
                        class="btn btn-sm btn-outline-primary"
                      >
                        <AppIcon name="bi-box-arrow-right" class="bi bi-box-arrow-right me-1" />
                        {{ $t('checkout') }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="!loading && !currentAttendees.length" class="text-center py-5">
              <AppIcon name="bi-building-x" class="bi bi-building-x text-muted" style="font-size: 3rem;" />
              <p class="text-muted mt-3 mb-0">{{ $t('no_attendees') }}</p>
              <small class="text-muted">{{ $t('no_one_currently_in_gym') }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <ShowSubscription
      :show="showSubscriptionModal"
      :subscription="selectedSubscription"
      @close="showSubscriptionModal = false"
    />
  </div>
</template>

<script>
import helper from '../helper'
import ShowSubscription from './ShowSubscription.vue'

export default {
  name: 'SubscriptionOverview',
  components: {
    ShowSubscription
  },
  data() {
    return {
      totalSubscriptions: 0,
      activeSubscriptions: 0,
      canceledSubscriptions: 0,
      todayAttendance: 0,
      currentClientsInGym: 0,
      currentAttendees: [],
      loading: false,
      showSubscriptionList: false,
      selectedSubscription: null,
      showSubscriptionModal: false,
      autoRefreshInterval: null
    };
  },
  computed: {
    activePercentage() {
      if (!this.totalSubscriptions) return 0;
      return Math.round((this.activeSubscriptions / this.totalSubscriptions) * 100);
    },
    canceledPercentage() {
      if (!this.totalSubscriptions) return 0;
      return Math.round((this.canceledSubscriptions / this.totalSubscriptions) * 100);
    },
    occupancyPercentage() {
      if (!this.totalSubscriptions) return 0;
      return Math.round((this.currentClientsInGym / this.totalSubscriptions) * 100);
    }
  },
  async created() {
    await this.fetchData();
    // Auto-refresh every 30 seconds
    this.autoRefreshInterval = setInterval(() => {
      this.fetchData();
    }, 30000);
  },
  beforeUnmount() {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval);
    }
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;
        const response = await helper.customGet(`${process.env.VUE_APP_API}/subscriptions-overview`);
        const data = response.data;
        
        this.totalSubscriptions = data.totalSubscriptions;
        this.activeSubscriptions = data.activeSubscriptions;
        this.canceledSubscriptions = data.canceledSubscriptions;
        this.todayAttendance = data.todayAttendance;
        this.currentClientsInGym = data.currentClientsInGym;
        this.currentAttendees = data.currentAttendees || [];
      } catch (error) {
        window.vueOpener.open({
            message: this.$t('error_fetching_subscription_overview'),
            type: 'error',
            position: 'bottom'
          });
      } finally {
        this.loading = false;
      }
    },
    async refreshData() {
      await this.fetchData();
      window.vueOpener.open({
        message: this.$t('data_refreshed') || 'Data refreshed',
        type: 'success',
        position: 'bottom'
      });
    },
    formatDate(date) {
      return helper.formatDate(date);
    },
    formatTime(date) {
      return new Date(date).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    },
    calculateDuration(checkIn) {
      const now = new Date();
      const checkInTime = new Date(checkIn);
      const diffMs = now - checkInTime;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const remainingMins = diffMins % 60;
      
      if (diffHours > 0) {
        return `${diffHours}h ${remainingMins}m`;
      }
      return `${remainingMins}m`;
    },
    getInitials(name) {
      if (!name) return '?';
      const words = name.split(' ');
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    async handleCheckout(id) {
      try {
        this.loading = true;
        await helper.customPatch(`${process.env.VUE_APP_API}/attendance/${id}`, {
          checkOut: new Date().toISOString()
        }, { showToast: true });
        await this.fetchData();
      } catch (error) {
        window.vueOpener.open({
          message: this.$t('error_checkout_failed'),
          type: 'error',
          position: 'bottom'
        });
      } finally {
        this.loading = false;
      }
    },
    async handleBulkCheckout() {
      if (!confirm(this.$t('confirm_checkout_all') || 'Are you sure you want to check out all clients?')) {
        return;
      }
      try {
        this.loading = true;
        const attendanceIds = this.currentAttendees.map(a => a.id);
        await helper.customPost(`${process.env.VUE_APP_API}/attendance/bulk-checkout`, {
          attendance_ids: attendanceIds
        }, { showToast: true });
        await this.fetchData();
      } catch (error) {
        window.vueOpener.open({
          message: this.$t('error_bulk_checkout_failed') || 'Failed to check out all clients',
          type: 'error',
          position: 'bottom'
        });
      } finally {
        this.loading = false;
      }
    },
    viewSubscriptionDetails(subscription) {
      // Show subscription details in a modal
      this.selectedSubscription = subscription;
      this.showSubscriptionModal = true;
    },
    getProgressPercentage(value, total) {
      if (!total || total === 0) return 0;
      return Math.round((value / total) * 100);
    }
  }
};
</script>

<style scoped>
.subscription-overview {
  padding: 0.5rem;
}

/* Metric Cards */
.metric-card {
  transition: all 0.3s ease;
  cursor: pointer;
}

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.metric-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.hover-lift:hover {
  transform: translateY(-2px);
}

/* Avatar Circle */
.avatar-circle {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.75rem;
}

/* Spinning Animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spinning {
  animation: spin 1s linear infinite;
}

/* Card Styles */
.card {
  border-radius: 0.5rem;
}

.card-header {
  background-color: #fff;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.25rem;
}

.card-body {
  padding: 1.25rem;
}

/* Table Styles */
.table {
  margin-bottom: 0;
}

.table thead th {
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #dee2e6;
}

.table tbody tr {
  transition: background-color 0.2s ease;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

/* Progress Bars */
.progress {
  border-radius: 0.25rem;
  background-color: #e9ecef;
}

.progress-bar {
  transition: width 0.6s ease;
}

/* Status Items */
.status-item {
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease;
}

.status-item:hover {
  background-color: #f8f9fa;
}

/* Badges */
.badge {
  padding: 0.375rem 0.75rem;
  font-weight: 600;
  border-radius: 0.375rem;
}

/* Button Styles */
.btn {
  transition: all 0.2s ease;
  font-weight: 500;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn:active:not(:disabled) {
  transform: translateY(0);
}

.btn-primary {
  background-color: #004aad;
  border-color: #004aad;
}

.btn-primary:hover {
  background-color: #252525;
  border-color: #252525;
}

.btn-outline-primary {
  color: #334155;
  border-color: #cbd5e1;
}

.btn-outline-primary:hover {
  background-color: #004aad;
  border-color: #004aad;
  color: white;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .subscription-overview {
    padding: 0.25rem;
  }
  
  .metric-card .card-body {
    padding: 0.75rem;
  }
  
  .metric-card h2 {
    font-size: 1.5rem;
  }
  
  .table {
    font-size: 0.875rem;
  }
}

/* Empty State */
.text-center i.bi-building-x {
  opacity: 0.3;
}

/* SVG Circle Animation */
circle {
  transition: stroke-dasharray 0.6s ease;
}
</style>
